// Commons imports.
import Request from '../Common/API/InternalAPIClient'
import { SERVLET_COMMAND, ServletCmds } from '../../NonReact/constants/ServletCommands'
// Sub imports.
import { ReportDefs, RetrieveStatInfoRequestParameters } from './Types'
import {
    RptConfigurationDataResponseDto,
    RptConfigurationDataAddDto,
    RptConfigurationDataUpdateDto,
    RptFilterEntityInfo
} from '../Common/Types/Types'
import { AvailableStatisticsFieldsArray } from './FieldsTab/Types'
import { SERVLET_ACTION, ServletActions } from '../../NonReact/constants/React/ServletActions/ServletActions'
import { mapRptConfigurationDataAddDtoToFormData, mapRptConfigurationDataUpdateDtoToFormData } from './ApiMapper'

/**
 * Retrieves the list of reports.
 *
 * @param servletPath Servlet path.
 */
export const retrieveReportProperties = (servletPath: string): Promise<ReportDefs> => {
    const params = new URLSearchParams()
    params.set(SERVLET_COMMAND, ServletCmds.RPT_TYPEDEFS)

    return Request(`${servletPath}?${params.toString()}`) as Promise<ReportDefs>
}

/**
 * Retrieves the filter entity info.
 *
 * @param servletPath Servlet path.
 * @param telSysID
 * @param partitionIDs
 * @param maxFilterListLimit The limit of the entities to retrieve in the list.
 */
export const retrieveFilterEntityInfo = (
    servletPath: string,
    telSysID: number,
    partitionIDs: string,
    maxFilterListLimit: number | undefined
): Promise<RptFilterEntityInfo> => {
    const params = new URLSearchParams()

    params.set(SERVLET_COMMAND, ServletCmds.RPT_FILTERENTITYINFO)
    params.set('IsReportCapabilityInfoCalculated', 'true')
    params.set('PartitionIDs', partitionIDs)
    params.set('TelephoneSystemID', String(telSysID))
    params.set('IncludeTelephoneSystemType', String(true))
    params.set('InvokeID', `UI-${Date.now()}`)

    if (maxFilterListLimit) {
        params.set('EntitiesMaxCount', String(maxFilterListLimit))
    }

    return Request(`${servletPath}?${params.toString()}`) as Promise<RptFilterEntityInfo>
}

/**
 * Retrieves existing report configuration data.
 *
 * @param servletPath Servlet path.
 * @param telSysID
 * @param partitionIDs
 */
export const retrieveReportConfigurationData = (
    servletPath: string,
    reportId: string
): Promise<RptConfigurationDataResponseDto> => {
    const params = new URLSearchParams()

    params.set(SERVLET_COMMAND, ServletCmds.RPT_CHANGE)
    params.set('InvokeID', `UI-${Date.now()}`)
    params.set('Action', `Get`)
    params.set('ResType', `JSON`)
    params.set('ID', reportId)
    params.set('IncludeScheduleData', 'true')

    return Request(`${servletPath}?${params.toString()}`) as Promise<RptConfigurationDataResponseDto>
}

/**
 * Updated report configuration for existing report.
 *
 * @param servletPath Servlet path.
 * @param rptConfigurationDataUpdateDto Data transfer object that contains data for the update request.
 *
 */
export const updateReportConfiguration = (
    servletPath: string,
    rptConfigurationDataUpdateDto: RptConfigurationDataUpdateDto
): Promise<RptConfigurationDataResponseDto> => {
    const params = new URLSearchParams()

    params.set(SERVLET_COMMAND, ServletCmds.RPT_CHANGE)
    params.set(SERVLET_ACTION, ServletActions.REPORT_CHANGE)

    const formData = mapRptConfigurationDataUpdateDtoToFormData(rptConfigurationDataUpdateDto)

    return Request(
        `${servletPath}?${params.toString()}`,
        'POST',
        formData,
        null,
        false
    ) as Promise<RptConfigurationDataResponseDto>
}

/**
 * Creates a new report.
 *
 * @param servletPath Servlet path.
 * @param rptConfigurationDataAddDto Data transfer object that contains data for the request.
 */
export const addReport = (
    servletPath: string,
    rptConfigurationDataAddDto: RptConfigurationDataAddDto
): Promise<RptConfigurationDataResponseDto> => {
    const params = new URLSearchParams()

    params.set(SERVLET_COMMAND, ServletCmds.RPT_CHANGE)
    params.set(SERVLET_ACTION, ServletActions.REPORT_ADD)

    const formData = mapRptConfigurationDataAddDtoToFormData(rptConfigurationDataAddDto)

    return Request(
        `${servletPath}?${params.toString()}`,
        'POST',
        formData,
        null,
        false
    ) as Promise<RptConfigurationDataResponseDto>
}

/**
 * Retrieves the stats info.
 *
 * @param servletPath Servlet path.
 * @param retrieveStatInfoRequestParameters Object containing all report properties that are required by statInfo API.
 */
export const retrieveAvailableStatisticsFields = (
    servletPath: string,
    retrieveStatInfoRequestParameters: RetrieveStatInfoRequestParameters
): Promise<AvailableStatisticsFieldsArray> => {
    const params = new URLSearchParams()

    params.set(SERVLET_COMMAND, ServletCmds.RPT_STATINFO)
    params.set('ReportType', retrieveStatInfoRequestParameters.reportType)
    params.set('RptPropsFilterPartitionIDs', retrieveStatInfoRequestParameters.partitionIDs)
    params.set('RptPropsFilterTelephoneSystemID', retrieveStatInfoRequestParameters.telSysID)
    params.set('OrderingType', '8') // Hardcoded default value that is not possible to configure in UI.
    params.set('RptPropsStatisticSettingSvcLvlAnsThres', retrieveStatInfoRequestParameters.svcLvlAnsThres)
    params.set('RptPropsStatisticSettingCallsInAnsThres1', retrieveStatInfoRequestParameters.callsInAnsThres1)
    params.set('RptPropsStatisticSettingCallsInAnsThres2', retrieveStatInfoRequestParameters.callsInAnsThres2)
    params.set('RptPropsStatisticSettingCallsInAnsThres3', retrieveStatInfoRequestParameters.callsInAnsThres3)
    params.set('RptPropsStatisticSettingCallsInAnsThres4', retrieveStatInfoRequestParameters.callsInAnsThres4)
    params.set('RptPropsStatisticSettingCallsInAnsThres5', retrieveStatInfoRequestParameters.callsInAnsThres5)
    params.set('RptPropsStatisticSettingCallsInAnsThres6', retrieveStatInfoRequestParameters.callsInAnsThres6)
    params.set('RptPropsStatisticSettingSvcLvlAbndThres', retrieveStatInfoRequestParameters.svcLvlAnsThres)
    params.set('RptPropsStatisticSettingCallsInAbndThres1', retrieveStatInfoRequestParameters.callsInAbndThres1)
    params.set('RptPropsStatisticSettingCallsInAbndThres2', retrieveStatInfoRequestParameters.callsInAbndThres2)
    params.set('RptPropsStatisticSettingCallsInAbndThres3', retrieveStatInfoRequestParameters.callsInAbndThres3)
    params.set('RptPropsStatisticSettingCallsInAbndThres4', retrieveStatInfoRequestParameters.callsInAbndThres4)
    params.set('RptPropsStatisticSettingCallsInAbndThres5', retrieveStatInfoRequestParameters.callsInAbndThres5)
    params.set('RptPropsStatisticSettingCallsInAbndThres6', retrieveStatInfoRequestParameters.callsInAbndThres6)
    params.set('RptPropsStatisticSettingACDNACode01', retrieveStatInfoRequestParameters.ACDNACode1)
    params.set('RptPropsStatisticSettingACDNACode02', retrieveStatInfoRequestParameters.ACDNACode2)
    params.set('RptPropsStatisticSettingACDNACode03', retrieveStatInfoRequestParameters.ACDNACode3)
    params.set('RptPropsStatisticSettingACDNACode04', retrieveStatInfoRequestParameters.ACDNACode4)
    params.set('RptPropsStatisticSettingACDNACode05', retrieveStatInfoRequestParameters.ACDNACode5)
    params.set('RptPropsStatisticSettingACDNACode06', retrieveStatInfoRequestParameters.ACDNACode6)
    params.set('RptPropsStatisticSettingACDNACode07', retrieveStatInfoRequestParameters.ACDNACode7)
    params.set('RptPropsStatisticSettingACDNACode08', retrieveStatInfoRequestParameters.ACDNACode8)
    params.set('RptPropsStatisticSettingACDNACode09', retrieveStatInfoRequestParameters.ACDNACode9)
    params.set('RptPropsStatisticSettingACDNACode10', retrieveStatInfoRequestParameters.ACDNACode10)
    params.set('RptPropsStatisticSettingACDNACode11', retrieveStatInfoRequestParameters.ACDNACode11)
    params.set('RptPropsStatisticSettingACDNACodeUseCodeDesc', retrieveStatInfoRequestParameters.ACDNACodeUseCodeDesc)
    params.set('InvokeID', `UI-${Date.now()}`)

    return Request(`${servletPath}?${params.toString()}`) as Promise<AvailableStatisticsFieldsArray>
}
